import type { Tier } from '@/analysis/wsTypes'

/** Brand palette for Hex view (shared chrome tokens for post-v1 app-wide migration). */
export const HEX_BRAND = {
  slate: '#2A3A4B',
  gridBg: '#212429',
  red: '#C12014',
  yellow: '#F1A026',
  green: '#268218',
  /** Inactive / no-data hex main fill (darker than brand slate). */
  noDataTop: '#1a222c',
  /** Slightly darker core so inactive radials still read like severity layers. */
  noDataCore: '#141a22',
  /** Top cap when all layers are severe or all green (same fill per brand spec). */
  statusTop: '#2A3A4B',
  arrowBg: '#2A3A4B',
  arrowBorder: '#FFFFFF',
} as const

/**
 * Severity layer fills: radial core (slate) → rim (severity).
 * Applied at LAYER_OPACITY on the stack.
 */
export const HEX_COLORS = {
  healthy: { rim: HEX_BRAND.green, core: HEX_BRAND.slate },
  warn:    { rim: HEX_BRAND.yellow, core: HEX_BRAND.slate },
  crit:    { rim: HEX_BRAND.red, core: HEX_BRAND.slate },
  /** No-data: same recipe as severity; main colour is noDataTop on the rim. */
  info:    { rim: HEX_BRAND.noDataTop, core: HEX_BRAND.noDataCore },
} as const

export type HexTone = keyof typeof HEX_COLORS

/** Spec: severity hex layers render at 75% opacity when not hovered. */
export const LAYER_OPACITY = 0.75

export const SQRT3 = Math.sqrt(3)

/**
 * Pointy-top equilateral hex: width = √3 · R, height = 2 · R.
 * Outer width = Hex 5 flat-to-flat (135.33 → ~156 height per brand spec).
 */
export const CELL_W = 135.33
export const HEX_R = CELL_W / SQRT3
export const CELL_H = 2 * HEX_R
/** True equilateral aspect (height / width). */
export const HEX_ASPECT = 2 / SQRT3

/**
 * Collapsed stack widths largest → smallest (Hex 5 … Hex 1).
 * Rank mapping still uses worst → smallest, best → largest.
 */
export const LAYER_WIDTHS = [135.33, 121.33, 108, 94, 80] as const
export const INFO_CAP_WIDTH = 64
/** Smaller solid detail hexes (centered on each expanded background). */
export const INNER_HEX_WIDTH = 72
export const EXPANDED_BG_WIDTH = CELL_W

/** Honeycomb packing — keep a clear gap on the #212429 grid. */
export const GRID_GAP_PX = 16
/** Center-to-center horizontal step (pointy-top). */
export const COL_W = CELL_W + GRID_GAP_PX
/** Center-to-center vertical step = 1.5·R + gap (pointy-top honeycomb). */
export const ROW_H = 1.5 * HEX_R + GRID_GAP_PX
/** Inset so hover-scale / edge tiles stay inside the scroll viewport (~8px over prior). */
export const GRID_PAD_X = 8
export const GRID_PAD_Y = 26
export const EXPANDED_SLOT_COLS = 3
export const EXPANDED_BG_ALPHA = 0.72

/** Whole-tile hover scale consumes ~½ of the inter-hex gap. */
export const HOVER_SCALE = 1 + (GRID_GAP_PX * 0.5) / CELL_W
export const EASE_BOUNCE = 'cubic-bezier(0.34, 1.56, 0.64, 1)'
export const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)'
export const EASE_INOUT = 'cubic-bezier(0.45, 0, 0.55, 1)'

/** Solid fills for expanded inner stat hexes. */
export const HEX_SOLID: Record<HexTone, string> = {
  healthy: HEX_BRAND.green,
  warn:    HEX_BRAND.yellow,
  crit:    HEX_BRAND.red,
  info:    HEX_BRAND.noDataTop,
}

export const ARROW_SRC = '/hex/arrow.png'

// Tier convention (app-wide): 0 = critical, 1 = warning, 2 = healthy.
export function tierTone(tier: Tier | null | undefined): HexTone {
  if (tier === 0) return 'crit'
  if (tier === 1) return 'warn'
  if (tier === 2) return 'healthy'
  return 'info'
}

/**
 * Top / info-cap fill from the brand spec:
 * - no tier data → neutral grey (no-telemetry ≠ critical)
 * - any known tiers → status slate
 */
export function topCapFill(tiers: Array<Tier | null | undefined>): string {
  const known = tiers.filter((t): t is Tier => t === 0 || t === 1 || t === 2)
  if (known.length === 0) return HEX_BRAND.noDataTop
  return HEX_BRAND.statusTop
}

export function hexGradientId(tone: HexTone, suffix: string): string {
  return `hex-grad-${tone}-${suffix}`
}

/** Height of an equilateral pointy-top hex given its flat-to-flat width. */
export function hexHeightForWidth(width: number): number {
  return width * HEX_ASPECT
}

/**
 * Pointy-top equilateral hex vertices in a local viewBox
 * of size (√3 · R) × (2 · R) with center at (√3·R/2, R).
 * Angles start at -30° so flats are left/right.
 */
export function equilateralHexPath(width: number): { path: string; height: number; vb: string } {
  const height = hexHeightForWidth(width)
  const cx = width / 2
  const cy = height / 2
  const r = height / 2 // = HEX_R for outer
  const pts = Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 180) * (60 * i - 30)
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const
  })
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(3)} ${p[1].toFixed(3)}`).join(' ') + ' Z'
  return { path, height, vb: `0 0 ${width} ${height}` }
}
